import {
  forwardRef,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import MapControl, { MapControlRef } from "./controls/MapControl";
import { Fill, Stroke, Style } from "ol/style";
import { osm, vector, xyz } from "./source";
import { fromLonLat, get } from "ol/proj";
import GeoJSON from "ol/format/GeoJSON";
import { Feature, Map as MapObject } from "ol";
import Point from "ol/geom/Point";
import { useState } from "react";
import CircleStyle from "ol/style/Circle";
import NavigatorPosition from "../../models/navigatorPosition";
import Navigator from "../../models/navigator";
import Layers from "./layers/Layers";
import TileLayer from "./layers/TileLayer";
import VectorLayer from "./layers/VectorLayer";
import Controls from "./controls/Controls";
import FullScreenControl from "./controls/FullScreenControl";
import SessionGpsPosition from "../../models/sessionGpsPosition";
import Navigable, {
  NavigatorMove,
  SetFilters,
  SetTableFilters,
  SetThematicMapFilters,
} from "../../models/navigable";
import LogService from "../../services/logService";
import LogDirectory from "../../services/logDirectory";
import mapService from "../../services/mapService";
import RoadLayer from "../../models/roadLayer";
import imageService from "../../services/imageService";
import mvt from "./source/mvt";
import VectorTileLayer from "./layers/VectorTileLayer";
import Filter from "../../models/filter";
import { transform } from "ol/proj";
import GpsPosition from "../../models/gpsPosition";
import appSettings from "../../appSettings";
import ProjectInfo from "../../models/projectInfo";
import LoadingIndicator from "../common/LoadingIndicator";
import * as olSource from "ol/source";
import RenderFeature from "ol/render/Feature";
import LayersIcon from "@mui/icons-material/Layers";
import MapLayers from "./MapLayers";
import TableColum from "../../models/tableColumn";
import tableService from "../../services/tableService";
import mapLayerService from "../../services/mapLayerService";
import MapLayer from "../../models/mapLayer";
import MapLayerType from "../../models/mapLayerType";
import MapLayerVisibility from "../../models/mapLayerVisibility";
import ThematicMapLayerRangeType from "../../models/thematicMapLayerRangeType";
import ThematicMapLayerLegend from "./thematic-maps/ThematicMapLayerLegend";
import User from "../../models/user";
import UserContext from "../../contexts/userContext";
import numberHelper from "../../util/numberHelper";
import { Geometry } from "ol/geom";
import VectorSource from "ol/source/Vector";
import { FeatureLike } from "ol/Feature";
import ThematicMapLayerRange from "../../models/thematicMapLayerRange";
import SegmentInfo from "../../models/segmentInfo";
import roadService from "../../services/roadService";
import unitConverter from "../../util/units/unitConverter";
import Tooltip from "../common/Tooltip";
import { Tooltip as BootstrapTooltip } from "bootstrap";
import ThematicMapLayerFilters from "../../models/thematicMapLayerFilters";
import TableManager from "../../models/tableManager";
import filterHelper from "../../models/filterHelper";
import FilterIcon from "@mui/icons-material/FilterAlt";
import GeospatialMapLayerType from "../../models/geospatialMapLayerType";
import MapLayerIndex from "../../models/mapLayerIndex";

const logService = new LogService(LogDirectory.EnableMapLogging);

const styles = {
  PolylineCurrent: new Style({
    stroke: new Stroke({
      color: "blue",
      width: 9,
    }),
  }),
  PolylineActive: new Style({
    stroke: new Stroke({
      color: "#16B173",
      width: 4,
    }),
  }),
  PolylineInactive: new Style({
    stroke: new Stroke({
      color: "#9A656F",
      width: 4,
    }),
  }),
  CurrentPosition: new Style({
    image: new CircleStyle({
      radius: 7,
      fill: new Fill({
        color: "blue",
      }),
      stroke: new Stroke({
        color: "white",
        width: 3,
      }),
    }),
  }),
};

const CUSTOM_FEATURE_COLOR_PROPERY: string = "CUSTOM_FEATURE_COLOR_PROPERY";
const CUSTOM_FEATURE_THEMATIC_LAYER_NAME: string =
  "CUSTOM_FEATURE_THEMATIC_FIELD_NAME";
const CUSTOM_FEATURE_THEMATIC_LABEL: string = "CUSTOM_FEATURE_THEMATIC_LABEL";
const CUSTOM_FEATURE_IS_RENDERED: string = "CUSTOM_FEATURE_IS_RENDERED";

const selectStyle = new Style({
  stroke: new Stroke({
    color: "yellow", // changed dynamically
    width: 10,
  }),
});

let selectedFeature: RenderFeature | null = null;
let selectedFeatureLayer: any = null;

interface Props {
  navigator: Navigator;
  project: ProjectInfo;
  onMapLayersChanged: any;
  // children: React.ReactNode;
}

export interface MapRef {
  refresh: () => void;
}

const Map = forwardRef((props: Props, ref: Ref<MapRef>) => {
  // console.log("map-render()");

  const { navigator, project } = props;

  const mapControlRef = useRef<MapControlRef>(null);

  const user = useContext<User | undefined>(UserContext);

  const [currentRoad, setCurrentRoad] = useState<RoadLayer>();
  const [center, setCenter] = useState<number[]>([]);
  const [zoom, setZoom] = useState(0);

  const [currentPositionMarker] = useState<VectorSource<Geometry>>(
    vector({ features: [] }),
  );

  const [baseLayer, setBaseLayer] = useState<any>();

  const [networkMvtSource, setNetworkMvtSource] = useState<
    olSource.VectorTile | undefined
  >();

  const [thematicMapsMvtSource, setThematicMapsMvtSource] = useState<
    olSource.VectorTile | undefined
  >();

  const [filtersIdRoads, setFiltersIdRoads] = useState<number[]>([]);
  const [filters, setFilters] = useState<Filter[]>([]);
  const [tableFilters, setTableFilters] = useState<Filter[]>([]);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [noLocationFound, setNoLocationFound] = useState<boolean>(false);

  const [tableColumns, setTableColumns] = useState<TableColum[]>([]);
  const [mapLayers, setMapLayers] = useState<MapLayer[]>([]);
  const [showMapLayers, setShowMapLayers] = useState<boolean>(false);
  const [visibleThematicMapLayer, setVisibleThematicMapLayer] =
    useState<MapLayer>();

  const [segmentInfo, setSegmentInfo] = useState<SegmentInfo>(
    new SegmentInfo(),
  );

  const [thematicMapLayerFilters, setThematicMapLayerFilters] =
    useState<ThematicMapLayerFilters>();

  const [containerWidth, setContainerWidth] = useState<number>();

  // const [sessionGpsPositions, setSessionGpsPositions] = useState<
  //   SessionGpsPosition[]
  // >([]);

  // const [currentIdSession, setCurrentIdSession] = useState<number>();

  useImperativeHandle(ref, () => ({ refresh }));

  const refresh = () => {
    logService.log("map:refresh()");
    mapControlRef.current?.refresh();
  };

  useEffect(() => {
    logService.log("map:int()");

    setZoom(13);
    setBaseLayer(osm());

    let currentIdSession: number | null = null;
    let currentIdRoad: number | null = null;
    let currentBatchSessionGpsPositions: SessionGpsPosition[] = [];
    let currentBatchSessionGpsPositionIndex: number = -1;

    const navigable = new Navigable();
    navigable.id = "map";

    if (project.hasAllRoadsMBTiles) {
      const allRoadsMvtSource = mvt({
        url: `${appSettings.serverUrl}/maptileservice/xyz/all-roads?idProject=${project.idProject}&x={x}&y={y}&z={z}`,
        minZoom: 0,
        maxZoom: 14,
      });
      setNetworkMvtSource((prevMvtSrc) => {
        if (prevMvtSrc) {
          prevMvtSrc.dispose();
        }
        return allRoadsMvtSource;
      });
    } else {
      setNetworkMvtSource((prevMvtSrc) => {
        if (prevMvtSrc) {
          prevMvtSrc.dispose();
        }
        return undefined;
      });
    }

    if (project.hasThematicMaps) {
      const reportRoadsMvtSource = mvt({
        url: `${appSettings.serverUrl}/maptileservice/xyz/report-segments?idProject=${project.idProject}&x={x}&y={y}&z={z}`,
        minZoom: 0,
        maxZoom: 14,
      });
      setThematicMapsMvtSource((prevMvtSrc) => {
        if (prevMvtSrc) {
          prevMvtSrc.dispose();
        }
        return reportRoadsMvtSource;
      });
    } else {
      setThematicMapsMvtSource((prevMvtSrc) => {
        if (prevMvtSrc) {
          prevMvtSrc.dispose();
        }
        return undefined;
      });
    }

    const initMapLayers = async () => {
      const { data: tableColumns } = await tableService.getTableColumns(
        project.idProject,
      );

      setTableColumns(tableColumns);

      const mapLayers = await mapLayerService.getMapLayers(
        project.idProject,
        tableColumns,
      );

      setMapLayers(mapLayers);

      setVisibleThematicMapLayer(undefined);

      for (const mapLayer of mapLayers) {
        if (mapLayer.mapLayerType === MapLayerType.ThematicLayer) {
          if (mapLayer.isVisible) {
            setVisibleThematicMapLayer(mapLayer);
          }
        }
      }

      const { data: segmentInfo } = await roadService.getSegmentInfo(
        project.idProject,
      );

      setSegmentInfo(segmentInfo);
    };

    const loadData = async (navigatorPosition: NavigatorPosition) => {
      logService.log("map:loadData()");
      try {
        const { data: roadLayer } = await mapService.getRoadLayer(
          project.idProject,
          navigatorPosition.idRoad,
        );
        setCurrentRoad(roadLayer);

        const { data: sessionGpsPositionIndex } =
          await mapService.getSessionGpsPositionIndex(
            project.idProject,
            navigatorPosition.idSession,
            navigatorPosition.distance,
          );

        let startIndex =
          sessionGpsPositionIndex - imageService.preloaderBackCacheSize * 35;
        if (startIndex < 0) {
          startIndex = 0;
        }
        const stopIndex = startIndex + mapService.batchSize;

        const { data: sessionGpsPositions } =
          await mapService.getSessionGpsPositions(
            project.idProject,
            navigatorPosition.idSession,
            startIndex,
            stopIndex,
          );

        currentBatchSessionGpsPositions = sessionGpsPositions;

        logService.log("map:loadData()-done");
      } catch (error) {
        logService.log(error);
      }
    };

    const navigatorMove: NavigatorMove = async (
      navigatorPosition: NavigatorPosition,
    ) => {
      logService.log("map:navigator:move()");

      if (
        currentIdSession === navigatorPosition.idSession &&
        currentIdRoad === navigatorPosition.idRoad
      ) {
        currentBatchSessionGpsPositionIndex =
          NavigatorPosition.getClosestNavigatorPositionIndex(
            navigatorPosition,
            currentBatchSessionGpsPositions,
          );
      } else {
        currentBatchSessionGpsPositionIndex = -1;
        currentIdSession = navigatorPosition.idSession;
        currentIdRoad = navigatorPosition.idRoad;
      }

      let isLoadData = false;
      if (currentBatchSessionGpsPositionIndex === -1) {
        isLoadData = true;
        await loadData(navigatorPosition);
      }

      currentBatchSessionGpsPositionIndex =
        NavigatorPosition.getClosestNavigatorPositionIndex(
          navigatorPosition,
          currentBatchSessionGpsPositions,
        );

      if (currentBatchSessionGpsPositionIndex === -1) {
        currentBatchSessionGpsPositionIndex = 0;
      }

      if (currentBatchSessionGpsPositions.length === 0) {
        return;
      }

      const sessionGpsPosition =
        currentBatchSessionGpsPositions[currentBatchSessionGpsPositionIndex];

      if (sessionGpsPosition.gpsPosition !== null) {
        const lonLatPosition = [
          sessionGpsPosition.gpsPosition.longitude,
          sessionGpsPosition.gpsPosition.latitude,
        ];

        if (isLoadData) {
          setCenter(lonLatPosition);
        }

        mapControlRef.current?.checkPositionInView(lonLatPosition);

        const currentPositionFeature = new Feature({
          geometry: new Point(fromLonLat(lonLatPosition)),
        });
        currentPositionFeature.setStyle(styles.CurrentPosition);

        currentPositionMarker.forEachFeature((f) => f.dispose());
        currentPositionMarker.clear();
        currentPositionMarker.addFeature(currentPositionFeature);
      }

      navigator.setGpsPosition(sessionGpsPosition.gpsPosition);
    };
    navigable.navigatorMove = navigatorMove;

    const navigatorSetFilters: SetFilters = (filters: Filter[]) => {
      setFiltersIdRoads(navigator.filtersIdRoads);
      setFilters(navigator.filters);
      return Promise.resolve();
    };
    navigable.setFilters = navigatorSetFilters;

    const navigatorSetTableFilters: SetTableFilters = (filters: Filter[]) => {
      setTableFilters(navigator.tableFilters);
      return Promise.resolve();
    };
    navigable.setTableFilters = navigatorSetTableFilters;

    const navigatorSetThematicMapFilters: SetThematicMapFilters = (
      thematicMapLayerFilters: ThematicMapLayerFilters | undefined,
    ) => {
      setThematicMapLayerFilters(thematicMapLayerFilters);
    };
    navigable.setThematicMapFilters = navigatorSetThematicMapFilters;

    navigator.attach(navigable);

    // this sould be called on open/close window
    if (
      navigator.navigatorPosition &&
      navigator.navigatorPosition.idProject === project.idProject
    ) {
      logService.log("map:navigator:move()-init");
      navigatorMove(navigator.navigatorPosition);
    }

    initTooltips();

    // setTimeout(() => {
    //   const lonlat = [-111.43163717989924, 35.85683404274522];
    //   setCurrentPosition(lonlat);
    //   setCurrentPositionMarker(getCurrentPositionMarker(lonlat));
    //   setZoom(9);
    // }, 10000);

    initMapLayers();

    return () => {
      navigator.setGpsPosition(null);
      navigator.detach(navigable);
    };
  }, [navigator, project, currentPositionMarker]);

  const initTooltips = () => {
    if (!containerRef.current) {
      return;
    }

    // console.log("map-initTooltips()");

    setTimeout(() => {
      if (!containerRef.current) {
        return;
      }

      // hack to allow some time for the open layers full screen control to be added dynamically to the dom

      Array.from(
        containerRef.current.querySelectorAll(
          'button[class="ol-full-screen-false"]',
        ),
      ).forEach((tooltipNode) => {
        new BootstrapTooltip(tooltipNode, {
          trigger: "hover",
          placement: "bottom",
        });
      });
    }, 500);
  };

  const getLayers = useCallback((): olSource.VectorTile[] => {
    let layers = [];

    if (networkMvtSource) {
      layers.push(networkMvtSource);
    }

    if (thematicMapsMvtSource) {
      layers.push(thematicMapsMvtSource);
    }

    return layers;
  }, [networkMvtSource, thematicMapsMvtSource]);

  const onReloadMapLayers = async () => {
    const mapLayers = await mapLayerService.getMapLayers(
      project.idProject,
      tableColumns,
    );

    setMapLayers(mapLayers);

    setVisibleThematicMapLayer(undefined);

    for (const mapLayer of mapLayers) {
      if (mapLayer.mapLayerType === MapLayerType.ThematicLayer) {
        if (mapLayer.isVisible) {
          setVisibleThematicMapLayer(mapLayer);
        }
      }
    }
  };

  const onCreateMapLayer = async (mapLayer: MapLayer) => {
    if (!user?.isDemoUser) {
      if (mapLayer.mapLayerType === MapLayerType.ThematicLayer) {
        // add last

        const indexes: number[] = mapLayers.map((ml) => ml.index);
        mapLayer.index = Math.max(...indexes) + 1;

        // make other thematic map layers invisible

        const mapLayerVisibilities: MapLayerVisibility[] = [];

        for (const otherThematicMapLayer of mapLayers.filter(
          (ml) =>
            ml.mapLayerType === MapLayerType.ThematicLayer &&
            ml.idMapLayer !== mapLayer.idMapLayer,
        )) {
          const mapLayerVisibility = new MapLayerVisibility();
          mapLayerVisibility.idMapLayer = otherThematicMapLayer.idMapLayer;
          mapLayerVisibility.isVisible = false;
          mapLayerVisibilities.push(mapLayerVisibility);
        }

        await mapLayerService.updateMapLayerVisibilities(mapLayerVisibilities);
      } else if (mapLayer.mapLayerType === MapLayerType.GeospatialLayer) {
        // add in the begining

        mapLayer.index = 0;

        // move rest down

        const mapLayerIndexes: MapLayerIndex[] = [];

        for (let index = 0; index < mapLayers.length; index++) {
          const mapLayerIndex = new MapLayerIndex();
          mapLayerIndex.idMapLayer = mapLayers[index].idMapLayer;
          mapLayerIndex.index = index + 1;
          mapLayerIndexes.push(mapLayerIndex);
        }

        await mapLayerService.updateMapLayerIndexes(mapLayerIndexes);
      }

      await mapLayerService.createMapLayer(mapLayer);
      await onReloadMapLayers();
      props.onMapLayersChanged();
    } else {
      if (mapLayer.mapLayerType === MapLayerType.ThematicLayer) {
        for (const otherThematicMapLayer of mapLayers.filter(
          (ml) => ml.mapLayerType === MapLayerType.ThematicLayer,
        )) {
          otherThematicMapLayer.isVisible = false;
        }

        setVisibleThematicMapLayer(mapLayer);
      }

      mapLayer.idMapLayer = numberHelper.getRandomInt(1000);

      const newMapLayers = [...mapLayers];
      newMapLayers.push(mapLayer);

      setMapLayers(newMapLayers);
    }
  };

  const onUpdateMapLayer = async (mapLayer: MapLayer) => {
    if (!user?.isDemoUser) {
      await mapLayerService.updateMapLayer(mapLayer);
      await onReloadMapLayers();
      props.onMapLayersChanged();
    } else {
      const newMapLayers = mapLayers.filter(
        (ml) => ml.idMapLayer !== mapLayer.idMapLayer,
      );

      newMapLayers.push(mapLayer);

      setMapLayers(newMapLayers);

      if (mapLayer.mapLayerType === MapLayerType.ThematicLayer) {
        setVisibleThematicMapLayer(mapLayer);
      }
    }
  };

  const onDeleteMapLayer = async (mapLayer: MapLayer) => {
    if (!user?.isDemoUser) {
      await mapLayerService.deleteMapLayer(mapLayer.idMapLayer);
      await onReloadMapLayers();
      props.onMapLayersChanged();
    } else {
      const newMapLayers = mapLayers.filter(
        (ml) => ml.idMapLayer !== mapLayer.idMapLayer,
      );

      setMapLayers(newMapLayers);

      if (mapLayer.mapLayerType === MapLayerType.ThematicLayer) {
        setVisibleThematicMapLayer(undefined);
      }
    }
  };

  const clearFeatureRenderProperties = (feature: FeatureLike) => {
    feature.getProperties()[CUSTOM_FEATURE_COLOR_PROPERY] = undefined;
    feature.getProperties()[CUSTOM_FEATURE_THEMATIC_LAYER_NAME] = undefined;
    feature.getProperties()[CUSTOM_FEATURE_THEMATIC_LABEL] = undefined;
    feature.getProperties()[CUSTOM_FEATURE_IS_RENDERED] = undefined;
  };

  const onUpdateMapLayerVisibility = (
    mapLayer: MapLayer,
    isVisible: boolean,
    mapLayers: MapLayer[],
    user: User | undefined,
  ) => {
    const mapLayerVisibilities: MapLayerVisibility[] = [];

    const newMapLayers = structuredClone(mapLayers);

    const newMapLayer = newMapLayers.find(
      (ml) => ml.idMapLayer === mapLayer.idMapLayer,
    );

    if (newMapLayer) {
      newMapLayer.isVisible = isVisible;

      const mapLayerVisibility = new MapLayerVisibility();
      mapLayerVisibility.idMapLayer = newMapLayer.idMapLayer;
      mapLayerVisibility.isVisible = newMapLayer.isVisible;
      mapLayerVisibilities.push(mapLayerVisibility);
    }

    if (mapLayer.mapLayerType === MapLayerType.ThematicLayer) {
      if (isVisible) {
        setVisibleThematicMapLayer(mapLayer);

        for (const otherThematicMapLayer of newMapLayers.filter(
          (ml) =>
            ml.mapLayerType === MapLayerType.ThematicLayer &&
            ml.idMapLayer !== mapLayer.idMapLayer,
        )) {
          otherThematicMapLayer.isVisible = false;

          const mapLayerVisibility = new MapLayerVisibility();
          mapLayerVisibility.idMapLayer = otherThematicMapLayer.idMapLayer;
          mapLayerVisibility.isVisible = false;
          mapLayerVisibilities.push(mapLayerVisibility);
        }
      } else {
        setVisibleThematicMapLayer(undefined);
      }
    }

    setMapLayers(newMapLayers);

    if (!user?.isDemoUser) {
      mapLayerService.updateMapLayerVisibilities(mapLayerVisibilities);
      // await onReloadMapLayers();
    }
  };

  const onUpdateLayersOrder = useCallback(
    (orderedMapLayerIds: number[]) => {
      const mapLayerIndexes: MapLayerIndex[] = [];
      const orderedMapLayers: MapLayer[] = [];

      for (const id of orderedMapLayerIds) {
        const mapLayer = mapLayers.find((ml) => ml.idMapLayer === id);
        if (mapLayer) {
          const newMapLayer = structuredClone(mapLayer);
          orderedMapLayers.push(newMapLayer);
        }
      }

      for (let index = 0; index < orderedMapLayers.length; index++) {
        const mapLayer = orderedMapLayers[index];
        mapLayer.index = index;

        const mapLayerIndex = new MapLayerIndex();
        mapLayerIndex.idMapLayer = mapLayer.idMapLayer;
        mapLayerIndex.index = mapLayer.index;
        mapLayerIndexes.push(mapLayerIndex);
      }

      setMapLayers(orderedMapLayers);

      if (!user?.isDemoUser) {
        mapLayerService.updateMapLayerIndexes(mapLayerIndexes);
        // await onReloadMapLayers();
      }
    },
    [mapLayers, user],
  );

  useEffect(() => {
    if (!thematicMapLayerFilters) {
      return;
    }

    const filterMapLayer = thematicMapLayerFilters.mapLayer;

    if (!filterMapLayer) {
      return;
    }

    const mapLayer = mapLayers.find(
      (ml) => ml.idMapLayer === filterMapLayer.idMapLayer,
    );

    if (mapLayer && mapLayer.isVisible) {
      return;
    }

    // console.log("thematicMapLayerFilter turn on layer");

    onUpdateMapLayerVisibility(filterMapLayer, true, mapLayers, user);
  }, [thematicMapLayerFilters, mapLayers, user, getLayers]);

  const onUpdateSegmentInfo = async (segmentInfo: SegmentInfo) => {
    const { data: updatedSegmentInfo } =
      await roadService.updateSegmentInfo(segmentInfo);
    setSegmentInfo(updatedSegmentInfo);
  };

  const projection = get("EPSG:3857");

  const isRenderedFeature = (
    navigatorPosition: NavigatorPosition,
    map: MapObject,
  ): boolean => {
    var extent = map.getView().calculateExtent(map.getSize());

    const layers = getLayers();
    for (const layer of layers) {
      const features = layer.getFeaturesInExtent(extent);

      const renderedFeatures = features.filter(
        (f) => f.getProperties()[CUSTOM_FEATURE_IS_RENDERED] === true,
      );

      let found = undefined;

      if (layer === thematicMapsMvtSource) {
        found = renderedFeatures.find(
          (f) => featureHasNavigatorPosition(f, navigatorPosition) === true,
        );
      } else {
        found = renderedFeatures.find(
          (f) => f.getProperties()["idRoad"] === navigatorPosition.idRoad,
        );
      }

      if (found) {
        return true;
      }
    }

    return false;
  };

  const onMapClick = async (evt: any) => {
    const lonlat = transform(evt.coordinate, "EPSG:3857", "EPSG:4326");
    const [lon, lat] = lonlat;

    const gpsPosition = new GpsPosition();
    gpsPosition.longitude = lon;
    gpsPosition.latitude = lat;

    const map = evt.map as MapObject;
    const zoom = map.getView().getZoom();
    const zoomLevel = Math.round(zoom ?? 0);

    console.log("MapClick lonlat, zoomLevel", lonlat, zoomLevel);

    setIsBusy(true);
    setNoLocationFound(false);

    const { data: navigatorPosition } =
      await mapService.getClosestNavigatorPositionForGpsPosition(
        project.idProject,
        gpsPosition,
        zoomLevel,
        [],
      );

    if (navigatorPosition) {
      if (!isRenderedFeature(navigatorPosition, map)) {
        setIsBusy(false);
        setNoLocationFound(true);
        setTimeout(() => setNoLocationFound(false), 2000);

        return;
      }

      if (navigator.filters.length > 0) {
        const isRoadInFilters = filtersIdRoads.find(
          (filterIdRoad) => filterIdRoad === navigatorPosition.idRoad,
        );
        if (!isRoadInFilters) {
          // clear filters
          await navigator.setFilters(project.idProject, [], false);
        }
      }

      await navigator.move(navigatorPosition);
      setIsBusy(false);
    } else {
      setIsBusy(false);
      setNoLocationFound(true);
      setTimeout(() => setNoLocationFound(false), 2000);
    }
  };

  const onMapPointerMove = (evt: any) => {
    const map = evt.map as MapObject;

    if (selectedFeature !== null) {
      selectedFeature.styleFunction = undefined;
      selectedFeature = null;

      if (infoRef.current) {
        const info = infoRef.current;
        info.style.visibility = "hidden";
      }

      selectedFeatureLayer.changed();
    }

    map.forEachFeatureAtPixel(evt.pixel, function (f: FeatureLike, layer) {
      const feature = f as RenderFeature;

      const color = feature.get(CUSTOM_FEATURE_COLOR_PROPERY);

      if (!color) {
        return;
      }

      selectedFeature = feature;
      selectedFeatureLayer = layer;

      feature.styleFunction = (f: any, res: any) => {
        selectStyle.getStroke().setColor(color);
        return selectStyle;
      };

      const tooltipOffsetLeft = 10;
      const tooltipOffsetTop = 14;

      if (infoRef.current) {
        const info = infoRef.current;
        info.style.left = evt.pixel[0] + tooltipOffsetLeft + "px";
        info.style.top = evt.pixel[1] + tooltipOffsetTop + "px";

        let toolTip = "";

        if (segmentInfo.showRoadName) {
          const roadName = feature.get("RoadName");
          toolTip += roadName + "</br>";
        }

        if (segmentInfo.showThematicField) {
          toolTip += `${
            feature.getProperties()[CUSTOM_FEATURE_THEMATIC_LAYER_NAME]
          }: ${feature.get(CUSTOM_FEATURE_THEMATIC_LABEL)}</br>`;
        }

        for (const field of segmentInfo.segmentInfoFields) {
          const tableColumn = tableColumns.find(
            (tc) => tc.columnName === field.fieldName,
          );

          if (!tableColumn) {
            continue;
          }

          let fieldValue = feature.get(tableColumn.columnName);

          fieldValue = unitConverter.convertValue(
            fieldValue,
            tableColumn.unitMeasure,
          );

          if (tableColumn.decimals !== -1) {
            fieldValue = numberHelper.numberToNDigits(
              fieldValue,
              tableColumn.decimals,
            );
          }

          toolTip += `${tableColumn.displayName}: ${fieldValue}</br>`;
        }

        if (toolTip) {
          info.innerHTML = toolTip;
          info.style.visibility = "visible";
        }
      }

      layer.changed();

      return true;
    });
  };

  const onChangeBasemap = (event: any) => {
    const layerType = event.target.value;

    if (layerType === "osm") {
      setBaseLayer(osm());
    }

    if (layerType === "aerial") {
      const xyzLayer = xyz({
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        maxZoom: 19,
      });
      setBaseLayer(xyzLayer);
    }
  };

  const allRoadsFeatureStyle = useCallback(
    (feature: RenderFeature, source: olSource.VectorTile) => {
      clearFeatureRenderProperties(feature);

      const networkMapLayer = mapLayers.find(
        (ml) => ml.mapLayerType === MapLayerType.NetworkLayer,
      );

      if (networkMapLayer) {
        if (!networkMapLayer.isVisible) {
          return;
        }
      }

      markFeatureAsRendered(feature);

      if (filtersIdRoads.length > 0) {
        const featureRoadId = feature.getId(); // or feature.getProperties()["idRoad"]
        const hasRoad = filtersIdRoads.find(
          (filterIdRoad) => filterIdRoad === featureRoadId,
        );
        if (hasRoad) {
          return styles.PolylineActive;
        } else {
          return styles.PolylineInactive;
        }
      }

      return styles.PolylineActive;

      // return new Style({
      //   stroke: new Stroke({
      //     color: "#FF13F0", // neon pink
      //     width: 4,
      //   }),
      // });
    },
    [filtersIdRoads, mapLayers],
  );

  const featureHasFilterValues = useCallback(
    (feature: RenderFeature | FeatureLike, filters: Filter[]) => {
      if (filters.length > 0) {
        for (const filter of filters) {
          if (!featureHasFilterValue(feature, filter)) {
            return false;
          }
        }
      }

      return true;
    },
    [],
  );

  const featureHasFilterValue = (
    feature: RenderFeature | FeatureLike,
    filter: Filter,
  ) => {
    const fieldValue = feature.getProperties()[filter.fieldName];
    return filterHelper.isFiledValueInFilter(fieldValue, filter);
  };

  const featureHasNavigatorPosition = (
    feature: RenderFeature | FeatureLike,
    navigatorPosition: NavigatorPosition,
  ) => {
    const idRoad = feature.getProperties()[TableManager.IdRoadHelperColumnName];
    const idSession =
      feature.getProperties()[TableManager.IdSessionHelperColumnName];
    const beginDistanceStamp =
      feature.getProperties()[TableManager.StartDistanceHelperColumnName];
    const endDistanceStamp =
      feature.getProperties()[TableManager.EndDistanceHelperColumnName];

    if (
      idRoad === navigatorPosition.idRoad &&
      idSession === navigatorPosition.idSession
    ) {
      const isBetween =
        navigatorPosition.distance >= beginDistanceStamp &&
        navigatorPosition.distance <= endDistanceStamp;

      if (isBetween) {
        return true;
      }
    }

    return false;
  };

  const reportRoadsFeatureStyle = useCallback(
    (feature: RenderFeature, source: olSource.VectorTile) => {
      clearFeatureRenderProperties(feature);

      if (!featureHasFilterValues(feature, filters)) {
        return;
      }

      if (!featureHasFilterValues(feature, tableFilters)) {
        return;
      }

      const thematicLayer = mapLayers.find(
        (l) => l.mapLayerType === MapLayerType.ThematicLayer && l.isVisible,
      );

      if (!thematicLayer || !thematicLayer.thematicMapLayer) {
        return;
      }

      const fieldValue =
        feature.getProperties()[thematicLayer.thematicMapLayer.fieldName];

      feature.getProperties()[CUSTOM_FEATURE_THEMATIC_LAYER_NAME] =
        thematicLayer.layerName;

      if (
        thematicLayer.thematicMapLayer.rangeType ===
        ThematicMapLayerRangeType.Continuous
      ) {
        for (const range of thematicLayer.thematicMapLayer
          .thematicMapLayerRanges) {
          if (thematicMapLayerFilters) {
            if (
              range.idThematicMapLayerRange !==
              thematicMapLayerFilters.thematicMapLayerRange
                ?.idThematicMapLayerRange
            ) {
              continue;
            }
          }

          const featureStyle = new Style({
            stroke: new Stroke({
              color: range.rangeColor,
              width: 4,
            }),
          });

          if (!range.hasRangeStart) {
            if (fieldValue <= range.rangeEndValue) {
              markFeatureAsRendered(feature);
              setRangeFeatureProperties(feature, range);
              return featureStyle;
            }
          } else if (!range.hasRangeEnd) {
            if (fieldValue > range.rangeStartValue) {
              markFeatureAsRendered(feature);
              setRangeFeatureProperties(feature, range);
              return featureStyle;
            }
          } else {
            if (
              fieldValue > range.rangeStartValue &&
              fieldValue <= range.rangeEndValue
            ) {
              markFeatureAsRendered(feature);
              setRangeFeatureProperties(feature, range);
              return featureStyle;
            }
          }
        }
      } else if (
        thematicLayer.thematicMapLayer.rangeType ===
        ThematicMapLayerRangeType.Discrete
      ) {
        for (const range of thematicLayer.thematicMapLayer
          .thematicMapLayerRanges) {
          if (thematicMapLayerFilters) {
            if (
              range.idThematicMapLayerRange !==
              thematicMapLayerFilters.thematicMapLayerRange
                ?.idThematicMapLayerRange
            ) {
              continue;
            }
          }

          const featureStyle = new Style({
            stroke: new Stroke({
              color: range.rangeColor,
              width: 4,
            }),
          });

          if (fieldValue === range.rangeValue) {
            markFeatureAsRendered(feature);
            setRangeFeatureProperties(feature, range);
            return featureStyle;
          }
        }
      }
    },
    [
      filters,
      tableFilters,
      thematicMapLayerFilters,
      mapLayers,
      featureHasFilterValues,
    ],
  );

  const setRangeFeatureProperties = (
    feature: RenderFeature,
    range: ThematicMapLayerRange,
  ) => {
    feature.getProperties()[CUSTOM_FEATURE_COLOR_PROPERY] = range.rangeColor;
    feature.getProperties()[CUSTOM_FEATURE_THEMATIC_LABEL] = range.rangeLabel;
  };

  const markFeatureAsRendered = (feature: RenderFeature) => {
    feature.getProperties()[CUSTOM_FEATURE_IS_RENDERED] = true;
  };

  const onLayersMenuClick = async () => {
    setShowMapLayers(!showMapLayers);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);

  setTimeout(() => {
    setContainerWidth(containerRef.current?.clientWidth);
  }, 500);

  const [xyzSources] = useState<Map<string, any>>(
    new globalThis.Map<string, any>(),
  );

  useEffect(() => {
    for (const ml of mapLayers) {
      if (
        ml.mapLayerType === MapLayerType.GeospatialLayer &&
        ml.geospatialMapLayer?.layerType === GeospatialMapLayerType.XyzTiles
      ) {
        addXyzSource(ml.geospatialMapLayer.layerUrl, xyzSources);
      }
    }
  }, [mapLayers, xyzSources]);

  const addXyzSource = (sourceUrl: string, sources: Map<string, any>) => {
    const exists = sources.has(sourceUrl);
    if (!exists) {
      sources.set(
        sourceUrl,
        xyz({
          url: sourceUrl,
          maxZoom: 26,
        }),
      );
    }
  };

  const getXyzSource = (xyzSourceUrl: string) => {
    addXyzSource(xyzSourceUrl, xyzSources);
    return xyzSources.get(xyzSourceUrl);
  };

  const renderMapLayer = (ml: MapLayer, index: number) => {
    switch (ml.mapLayerType) {
      case MapLayerType.GeospatialLayer:
        if (
          ml.geospatialMapLayer?.layerType ===
            GeospatialMapLayerType.XyzTiles &&
          ml.isVisible
        ) {
          return (
            <TileLayer
              key={ml.idMapLayer}
              source={
                ml.geospatialMapLayer
                  ? getXyzSource(ml.geospatialMapLayer.layerUrl)
                  : undefined
              }
              zIndex={index}
            />
          );
        }
        break;

      case MapLayerType.NetworkLayer:
        if (networkMvtSource) {
          return (
            <VectorTileLayer
              key={ml.idMapLayer}
              style={(feature: any) =>
                allRoadsFeatureStyle(feature, networkMvtSource)
              }
              source={networkMvtSource}
              zIndex={index}
            />
          );
        }
        break;

      case MapLayerType.CurrentRoadLayer:
        if (currentRoad && projection && ml.isVisible) {
          return (
            <VectorLayer
              key={ml.idMapLayer}
              style={styles.PolylineCurrent}
              source={vector({
                features: new GeoJSON().readFeatures(currentRoad.geometry, {
                  featureProjection: projection,
                }),
              })}
              zIndex={index}
            />
          );
        }
        break;

      case MapLayerType.ThematicLayer:
        if (thematicMapsMvtSource && ml.isVisible) {
          return (
            <VectorTileLayer
              key={ml.idMapLayer}
              style={(feature: any) =>
                reportRoadsFeatureStyle(feature, thematicMapsMvtSource)
              }
              source={thematicMapsMvtSource}
              zIndex={index}
            />
          );
        }
        break;

      default:
        break;
    }
  };

  return (
    <div ref={containerRef}>
      <MapControl
        ref={mapControlRef}
        center={center}
        zoom={zoom}
        onClick={onMapClick}
        onPointerMove={onMapPointerMove}
      >
        <Layers>
          <TileLayer source={baseLayer} zIndex={0} />
          {mapLayers.map((ml, index) => renderMapLayer(ml, index))}
          <VectorLayer source={currentPositionMarker} zIndex={99} />
        </Layers>
        <Controls>
          <FullScreenControl />
        </Controls>
      </MapControl>

      <div
        style={{
          position: "absolute",
          top: "4px",
          left: "4px",
          zIndex: "2",
        }}
      >
        <div className="d-flex">
          <Tooltip title="Map Layers" parentContainer={containerRef}>
            <div
              className={
                "image-button" // + (showMapLayers ? " image-button-active" : "")
              }
              style={{ marginBottom: "4px" }}
              onClick={onLayersMenuClick}
            >
              <LayersIcon style={{ height: "20px" }} />
            </div>
          </Tooltip>
          {tableFilters.length > 0 && (
            <div
              style={{ color: "#D0021B" }}
              title={"Filtered by " + filterHelper.filtersToText(tableFilters)}
            >
              <FilterIcon />
            </div>
          )}
        </div>
        <div>
          {showMapLayers && (
            <MapLayers
              project={project}
              mapLayers={mapLayers}
              tableColumns={tableColumns}
              segmentInfo={segmentInfo}
              onCreateMapLayer={onCreateMapLayer}
              onUpdateMapLayer={onUpdateMapLayer}
              onDeleteMapLayer={onDeleteMapLayer}
              onUpdateMapLayerVisibility={(
                mapLayer: MapLayer,
                isVisible: boolean,
              ) =>
                onUpdateMapLayerVisibility(mapLayer, isVisible, mapLayers, user)
              }
              onUpdateLayersOrder={onUpdateLayersOrder}
              onUpdateSegmentInfo={onUpdateSegmentInfo}
            />
          )}
        </div>
      </div>
      {visibleThematicMapLayer && (
        <div
          style={{
            position: "absolute",
            bottom: "4px",
            left: "4px",
            width: containerWidth ? containerWidth - 5 : containerWidth,
            overflow: "hidden",
          }}
        >
          <ThematicMapLayerLegend
            mapLayer={visibleThematicMapLayer}
            thematicMapLayerFilters={thematicMapLayerFilters}
          />
        </div>
      )}

      <div style={{ position: "absolute", top: "6px", right: "40px" }}>
        <Tooltip title="Base Map" parentContainer={containerRef}>
          <select style={{ padding: "2px 4px" }} onChange={onChangeBasemap}>
            <option value="osm">Streets</option>
            <option value="aerial">Aerial</option>
          </select>
        </Tooltip>
      </div>
      {isBusy && <LoadingIndicator className="map" />}
      {noLocationFound && (
        <div
          style={{
            position: "absolute",
            bottom: "0px",
            right: "0px",
            zIndex: "2",
            margin: "3px",
          }}
        >
          <div
            style={{ padding: "0px 2px", margin: "0px", lineHeight: "1.5" }}
            className="alert alert-danger"
          >
            No data found at selected location!
          </div>
        </div>
      )}
      <div className="map-info" ref={infoRef} />
    </div>
  );
});

export default Map;
